import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Info, ShieldAlert, FileText } from 'lucide-react';
import { docsApi, type DocValidationFinding } from '@/shared/docsApi';
import { format } from 'date-fns';

const DECISION_LABEL: Record<string, string> = {
  pass: 'Pass — no issues',
  pass_with_warnings: 'Pass with warnings',
  hard_block: 'Hard block — KYC cannot proceed',
};

const DECISION_TONE: Record<string, 'match' | 'warn' | 'mismatch'> = {
  pass: 'match',
  pass_with_warnings: 'warn',
  hard_block: 'mismatch',
};

export function DocValidationJobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ['doc-job', id],
    queryFn: () => docsApi.get(id!),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'queued' || s === 'processing' ? 5_000 : false;
    },
  });

  if (isLoading) return <div className="max-w-7xl mx-auto px-6 py-8 text-axis-ash">Loading…</div>;
  if (!data)      return <div className="max-w-7xl mx-auto px-6 py-8 text-axis-ash">Job not found.</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-6">
        <Link to="/docs" className="text-sm text-axis-ash hover:text-axis-ink inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="w-4 h-4" /> All validations
        </Link>
        <h1 className="text-2xl font-semibold text-axis-ink">Validation {data.id.slice(0, 12)}…</h1>
        <div className="text-sm text-axis-ash mt-1">
          {data.account_holder_name && (
            <>Account holder <strong>{data.account_holder_name}</strong> · </>
          )}
          status <strong>{data.status}</strong> ·
          submitted {format(new Date(data.created_at), 'd MMM yyyy, HH:mm:ss.SSS')}
        </div>
      </div>

      {/* Decision banner */}
      {data.report && (
        <div className={`card p-4 mb-6 ${
          DECISION_TONE[data.report.decision] === 'match'    ? 'bg-emerald-500/10 border-emerald-400/30' :
          DECISION_TONE[data.report.decision] === 'warn'     ? 'bg-amber-500/10 border-amber-400/30' :
                                                               'bg-rose-500/10 border-rose-400/30'
        }`}>
          <div className="flex items-center gap-3">
            {data.report.decision === 'pass' ? (
              <CheckCircle2 className="w-6 h-6 text-emerald-300" />
            ) : data.report.decision === 'pass_with_warnings' ? (
              <AlertTriangle className="w-6 h-6 text-amber-300" />
            ) : (
              <ShieldAlert className="w-6 h-6 text-rose-300" />
            )}
            <div className="flex-1">
              <div className="font-semibold text-axis-ink">{DECISION_LABEL[data.report.decision]}</div>
              <div className="text-sm text-axis-ash mt-0.5">
                {data.report.totals.documents_evaluated} document(s) evaluated ·
                {' '}{data.report.totals.hard_blocks} hard block(s) ·
                {' '}{data.report.totals.warnings} warning(s) ·
                {' '}{data.report.totals.infos} info
              </div>
            </div>
          </div>
        </div>
      )}

      {data.status === 'failed' && (
        <div className="card p-4 mb-6 bg-rose-500/10 border-rose-400/30 text-rose-300 text-sm">
          Pipeline failed: {data.error_message}
        </div>
      )}

      {/* Per-document sections */}
      {data.report?.per_document.map((doc, i) => (
        <div key={i} className="card mb-4">
          <div className="px-4 py-3 border-b border-axis-cloud-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-axis-ash" />
              <span className="font-medium text-axis-ink">{doc.filename}</span>
              <span className="pill bg-axis-cloud-100 text-axis-ash border border-axis-cloud-200">
                {doc.detected_type}
              </span>
            </div>
            <span className="text-xs text-axis-ash">
              {doc.findings.length} finding(s)
            </span>
          </div>
          {doc.findings.length === 0 ? (
            <div className="px-4 py-3 text-sm text-axis-ash inline-flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              No issues detected.
            </div>
          ) : (
            <ul className="divide-y divide-axis-cloud-200">
              {doc.findings.map((f, j) => <FindingRow key={j} f={f} />)}
            </ul>
          )}
        </div>
      ))}

      {/* Cross-document findings (not tied to a specific document) */}
      {data.report && (() => {
        const cross = data.report.findings.filter(f =>
          // crude approximation: findings whose code starts with 'cross.' or 'nominee.'
          /^cross\.|^nominee\./.test(f.code),
        );
        if (cross.length === 0) return null;
        return (
          <div className="card mb-4">
            <div className="px-4 py-3 border-b border-axis-cloud-200 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-axis-maroon" />
              <span className="font-medium text-axis-ink">Cross-document & nominee checks</span>
              <span className="text-xs text-axis-ash">({cross.length})</span>
            </div>
            <ul className="divide-y divide-axis-cloud-200">
              {cross.map((f, j) => <FindingRow key={j} f={f} />)}
            </ul>
          </div>
        );
      })()}
    </div>
  );
}

function FindingRow({ f }: { f: DocValidationFinding }) {
  const styles = {
    hard_block: { icon: XCircle,        cls: 'text-rose-300',   bg: 'bg-rose-500/10' },
    warn:       { icon: AlertTriangle,  cls: 'text-amber-300',  bg: 'bg-amber-500/10' },
    info:       { icon: Info,           cls: 'text-axis-ash',   bg: '' },
  }[f.severity];
  const Icon = styles.icon;
  return (
    <li className={`px-4 py-3 flex gap-3 ${styles.bg}`}>
      <Icon className={`w-5 h-5 shrink-0 ${styles.cls}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-axis-cloud-100 text-axis-ash">{f.code}</span>
          {f.field && <span className="text-xs text-axis-ash">field: {f.field}</span>}
        </div>
        <div className="text-sm text-axis-ink mt-1">{f.message}</div>
        {f.context && Object.keys(f.context).length > 0 && (
          <details className="mt-1">
            <summary className="text-xs text-axis-ash cursor-pointer hover:text-axis-ink">Context</summary>
            <pre className="text-xs mt-1 p-2 bg-axis-cloud-50 rounded overflow-x-auto">
              {JSON.stringify(f.context, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </li>
  );
}
